import React, { Component } from "react";
import { Link } from "react-router-dom";

// Components
import Icon from "../../components/Icon";
import Dropdown from "../../components/Dropdown/Dropdown";

// Helper
import { apiFetch, registerStudent, listGovernorates, listStates } from "../../Helper/Api";
import { hasModule } from "../../Helper/modules";
import Toast from "../../components/Toast/Toast";
import { t } from "../../Helper/i18n";

const properties = require("../../properties.json");

// Strings, not numbers: a native select handed back `e.target.value` as a
// string, the state has always held one, and submit still does Number(gender).
// Labels are i18n keys, read at render: the theme sets the language after this module loads.
const GENDERS = [
  { value: "1", label: "male" },
  { value: "2", label: "female" },
];

// Just the form -- the shell belongs to pages/Auth/AuthLayout.js. See the note
// there: the layout stays mounted while this swaps with SignIn, so moving
// between the two no longer rebuilds the screen. The tenant arrives as a prop,
// already resolved.
class SignUp extends Component {
  state = {
    fullName: "",
    email: "",
    password: "",
    studentPhone: "",
    parentPhone: "",
    governorateId: "",
    stateId: "",
    schoolStagesId: "",
    gender: "",

    governorates: [],
    states: [],
    schoolStages: [],

    buttonText: t("createTheAccount"),
    submitting: false,
    msg: null,
    // design-only: whether the password is shown
    showPass: false,
  };

  componentDidMount() {
    document.title = properties.site_name + " - " + t("newAccount");

    const { tenant } = this.props;
    if (!hasModule(tenant, "SCHOOL_STRUCTURE")) return;
    Promise.all([
      listGovernorates(),
      apiFetch("/lookup/school-stages/by-teacher", { query: { teacherUserId: tenant.teacherUserId } }),
    ])
      .then(([governorates, schoolStages]) => this.setState({ governorates, schoolStages }))
      .catch(() => {});
  }

  onGovernorateChange = (governorateId) => {
    // picking a different governorate invalidates the centre under it
    this.setState({ governorateId, stateId: "", states: [] });
    if (!governorateId) return;
    listStates(governorateId)
      .then((states) => this.setState({ states }))
      .catch(() => {});
  };

  submit = (e) => {
    e.preventDefault();
    const { fullName, email, password, studentPhone, parentPhone, stateId, schoolStagesId, gender } = this.state;

    if (!fullName || !email || !password) {
      this.setState({ msg: { text: t("fillRequired"), type: "warning" } });
      return;
    }
    if (password.length < 8) {
      this.setState({ msg: { text: t("shortPassword"), type: "warning" } });
      return;
    }

    this.setState({ submitting: true, buttonText: t("creating"), msg: null });

    registerStudent({
      fullName,
      email,
      password,
      mobile: studentPhone || null,
      fatherMobile: parentPhone || null,
      gender: gender ? Number(gender) : null,
      stateId: stateId ? Number(stateId) : null,
      schoolStagesId: schoolStagesId ? Number(schoolStagesId) : null,
      branchId: this.props.tenant.branchId,
    })
      .then(() => {
        this.setState({
          submitting: false,
          buttonText: t("createTheAccount"),
          msg: { text: t("accountCreated"), type: "success" },
        });
        setTimeout(() => {
          window.location.href = "/sign-in";
        }, 1200);
      })
      .catch((error) => {
        this.setState({
          submitting: false,
          buttonText: t("createTheAccount"),
          msg: { text: error.message || t("accountFailed"), type: "warning" },
        });
      });
  };

  render() {
    const { governorates, states, schoolStages, buttonText, submitting, msg, showPass } = this.state;

    // the centre list only exists once a governorate has been picked
    const centreLocked = !this.state.governorateId;
    // a gym (or any site without the school structure) asks for a name, e-mail, password, phone and gender only
    const school = hasModule(this.props.tenant, "SCHOOL_STRUCTURE");

    return (
      <div className="au-swap">
        <div className="au-head">
          <h1>{t("newAccount")}</h1>
          <p>{school ? t("signUpLeadSchool") : t("signUpLeadGym")}</p>
        </div>

        <form className="au-form" onSubmit={this.submit}>
          <div className="au-field" style={{ "--d": "100ms" }}>
            <label className="lb" htmlFor="full-name">{t("name")}</label>
            <div className="au-box">
              <span className="ic" aria-hidden="true"><Icon name="spark" /></span>
              <input
                type="text"
                id="full-name"
                autoComplete="name"
                value={this.state.fullName}
                onChange={(e) => this.setState({ fullName: e.target.value })}
              />
            </div>
          </div>

          <div className="au-field" style={{ "--d": "140ms" }}>
            <label className="lb" htmlFor="e-mail">{t("email")}</label>
            <div className="au-box">
              <span className="ic" aria-hidden="true"><Icon name="mail" /></span>
              <input
                type="email"
                id="e-mail"
                autoComplete="email"
                placeholder="name@example.com"
                value={this.state.email}
                onChange={(e) => this.setState({ email: e.target.value })}
              />
            </div>
          </div>

          <div className="au-field" style={{ "--d": "180ms" }}>
            <label className="lb" htmlFor="password">
              {t("password")}
              <span className="hint">{t("passHint")}</span>
            </label>
            <div className="au-box">
              <span className="ic" aria-hidden="true"><Icon name="lock" /></span>
              <input
                type={showPass ? "text" : "password"}
                id="password"
                autoComplete="new-password"
                placeholder="••••••••"
                value={this.state.password}
                onChange={(e) => this.setState({ password: e.target.value })}
              />
              <button
                type="button"
                className="au-reveal"
                onClick={() => this.setState({ showPass: !showPass })}
                aria-label={showPass ? t("hidePass") : t("showPass")}
              >
                <Icon name={showPass ? "eyeoff" : "eye"} />
              </button>
            </div>
          </div>

          {school && (
            <div className="au-pair">
              <div className="au-field is-compact" style={{ "--d": "220ms" }}>
                <label className="lb" htmlFor="city">{t("governorate")}</label>
                <Dropdown
                  id="city"
                  compact
                  placeholder={t("chooseGovernorate")}
                  value={this.state.governorateId}
                  options={governorates.map((g) => ({ value: g.id, label: g.name }))}
                  onChange={this.onGovernorateChange}
                />
              </div>

              <div className="au-field is-compact" style={{ "--d": "250ms" }}>
                <label className="lb" htmlFor="state">
                  {t("district")}
                  {centreLocked && <span className="hint">{t("afterGovernorate")}</span>}
                </label>
                <Dropdown
                  id="state"
                  compact
                  disabled={centreLocked}
                  placeholder={t("chooseDistrict")}
                  value={this.state.stateId}
                  options={states.map((s) => ({ value: s.id, label: s.name }))}
                  onChange={(stateId) => this.setState({ stateId })}
                />
              </div>
            </div>
          )}

          <div className="au-pair">
            <div className="au-field is-compact" style={{ "--d": "280ms" }}>
              <label className="lb" htmlFor="stud-phone">{school ? t("studentPhone") : t("mobile")}</label>
              <div className="au-box">
                <input
                  type="tel"
                  id="stud-phone"
                  autoComplete="tel"
                  value={this.state.studentPhone}
                  onChange={(e) => this.setState({ studentPhone: e.target.value })}
                />
              </div>
            </div>

            {school && (
              <div className="au-field is-compact" style={{ "--d": "310ms" }}>
                <label className="lb" htmlFor="parent-phone">{t("parentPhone")}</label>
                <div className="au-box">
                  <input
                    type="tel"
                    id="parent-phone"
                    value={this.state.parentPhone}
                    onChange={(e) => this.setState({ parentPhone: e.target.value })}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="au-pair">
            <div className="au-field is-compact" style={{ "--d": "340ms" }}>
              <label className="lb" htmlFor="gender">{t("gender")}</label>
              <Dropdown
                id="gender"
                compact
                placeholder={t("chooseGender")}
                value={this.state.gender}
                options={GENDERS.map((g) => ({ value: g.value, label: t(g.label) }))}
                onChange={(gender) => this.setState({ gender })}
              />
            </div>

            {school && (
              <div className="au-field is-compact" style={{ "--d": "370ms" }}>
                <label className="lb" htmlFor="grade">{t("grade")}</label>
                <Dropdown
                  id="grade"
                  compact
                  placeholder={t("chooseGrade")}
                  emptyLabel={t("noGrades")}
                  value={this.state.schoolStagesId}
                  options={schoolStages.map((s) => ({ value: s.id, label: s.name }))}
                  onChange={(schoolStagesId) => this.setState({ schoolStagesId })}
                />
              </div>
            )}
          </div>


          <button className="mu-btn is-gold au-submit" type="submit" disabled={submitting}>
            {submitting && <span className="spin" aria-hidden="true"></span>}
            {buttonText}
          </button>

          <Toast msg={msg} />

          {/* the tabs above already move between the two auth pages, so this
              slot carries the way back to the site instead */}
          <Link to="/" className="au-home">
            <Icon name="arrow" aria-hidden="true" />
            {t("backToSite")}
          </Link>
        </form>
      </div>
    );
  }
}

export default SignUp;
